import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Switch, useTheme } from '@mui/material';
import Colors from '../../library/Colors';

/**
 * The look of the webshop-admin configuration UI, decided by WHERE a piece
 * sits -- not by a user choice.
 *
 *   screens   (the config tabs, pages, features): the regular admin look,
 *             boxed cards in the page flow. Internally the `classic` tokens.
 *   modals    (every settings dialog, through DialogBody): the full-screen
 *             grey stage, every topic its own white surface stacked in one
 *             scroll, an index on the left that follows along, help text
 *             behind an info glyph unless "Toon uitleg" is on. Internally the
 *             `doorloop` tokens.
 *
 * DialogBody marks everything inside it as "modal"; the shared primitives
 * (ConfigCard, Section, PdiRow, SubLabel, ModalCardFlow, SectionRail) read
 * their tokens through useDesignVariant() and so follow automatically. The
 * dialog frame itself (paper, title, actions) is drawn outside DialogBody and
 * asks for the modal tokens explicitly with useModalDesign().
 *
 * History: five switchable demo variants (classic / werkblad / tabel /
 * panelen / doorloop) behind a SoftTouch-only toggle; 2026-09-24 the owner
 * settled on doorloop for every modal and classic for the screens, and the
 * toggle went.
 */

const HELP_KEY = 'sft_webshop_design_help';

// ---- tokens -----------------------------------------------------------------
// Everything below is derived from (variant, isDark). Kept as plain functions
// so consumers get stable objects per (variant, mode) via useMemo.

const RULE = { light: '#E3E5E8', dark: '#3A4654' };

const resolveTokens = (variant, isDark, showHelp) => {
    const rule = isDark ? RULE.dark : RULE.light;
    switch (variant) {
        case 'doorloop': {
            // every topic is a white surface on a grey stage: the frame can use
            // the whole screen without the settings floating in a void
            const surfaceBg = isDark ? '#2D333B' : '#FFFFFF';
            const surface = {
                backgroundColor: surfaceBg,
                border: `1px solid ${rule}`,
                borderRadius: 2,
                px: 4,
                py: 3,
            };
            return {
                variant,
                modalColumns: { xs: '1' },
                flowGap: 2,
                rail: true,
                railWidth: 240,
                railBorder: false,
                helpToggle: true,
                // the white of the cards; the rail's selected item uses it too
                surfaceBg,
                dialog: {
                    // the whole screen, and a fixed height so the dialog does
                    // not jump between modals of different length
                    paperSx: { borderRadius: 2, width: '94vw', maxWidth: 1700, height: '92vh', backgroundColor: isDark ? '#1E2430' : '#F4F6F8' },
                    // textTransform: the global `h2 { text-transform: lowercase }`
                    // in main.css would otherwise lowercase the dialog title
                    titleSx: { px: 3, pt: 2, pb: 1.5, textTransform: 'none' },
                    titleTypography: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 },
                    subtitleSx: { fontSize: '0.85rem', mt: 0.25 },
                    contentSx: { px: 3, py: 2.5 },
                    actionsSx: { px: 3, py: 1.5 },
                },
                card: { ...surface, boxShadow: 'none', '&:hover': { boxShadow: 'none', borderColor: rule } },
                cardHeader: { borderBottom: `1px solid ${rule}`, mb: 1.5, pb: 1 },
                cardTitle: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.25 },
                cardDescription: showHelp ? { fontSize: '0.875rem', mt: 0.5, lineHeight: 1.5 } : { display: 'none' },
                cardBodySpacing: 0.5,
                row: 'grid',
                rowSx: {
                    display: 'grid',
                    // label on the left, the control column takes the right ~40%:
                    // inputs stretch to fill it, switches sit on its right edge
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 40%)',
                    alignItems: 'center',
                    columnGap: 4,
                    py: 1.5,
                    borderBottom: `1px solid ${rule}`,
                    '&:last-of-type': { borderBottom: 'none' },
                },
                rowControlSx: {
                    justifySelf: 'stretch',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    minWidth: 0,
                    '& > .MuiTextField-root, & > .MuiFormControl-root, & > .MuiAutocomplete-root': { flex: 1 },
                },
                rowLabelSx: { fontSize: '0.95rem', fontWeight: 600 },
                rowHelpSx: { fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 },
                // quiet by default; the rail has a "Toon uitleg" switch
                helpAsTooltip: !showHelp,
                sublabel: {
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: 'text.primary',
                },
            };
        }
        default:
            // classic (screens): every primitive falls back to its own styling
            return {
                variant: 'classic',
                modalColumns: { xs: '1', lg: '2' },
                flowGap: 2,
                rail: false,
                card: null,
                cardHeader: null,
                cardTitle: null,
                cardDescription: null,
                cardBody: null,
                cardBodySpacing: 2,
                row: 'stack',
                rowSx: null,
                rowControlSx: null,
                rowLabelSx: null,
                rowHelpSx: null,
                sublabel: null,
            };
    }
};

// ---- context ----------------------------------------------------------------

// Per-browser preference, shared by every dialog: show help text under the
// labels instead of behind the info glyph.
const HelpContext = createContext({ showHelp: false, setShowHelp: () => {} });

// 'screen' outside a dialog, 'modal' inside DialogBody.
const SurfaceContext = createContext('screen');

const readHelp = () => {
    try { return window.localStorage.getItem(HELP_KEY) === '1'; } catch { return false; }
};

export const WebshopDesignProvider = ({ children }) => {
    const [showHelp, setShowHelpState] = useState(readHelp);
    const setShowHelp = useCallback((on) => {
        setShowHelpState(!!on);
        try { window.localStorage.setItem(HELP_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    }, []);
    const value = useMemo(() => ({ showHelp, setShowHelp }), [showHelp, setShowHelp]);
    return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};
WebshopDesignProvider.propTypes = { children: PropTypes.node };

const useResolved = (variant) => {
    const { showHelp, setShowHelp } = useContext(HelpContext);
    const isDark = useTheme().palette.mode === 'dark';
    const tokens = useMemo(() => resolveTokens(variant, isDark, showHelp), [variant, isDark, showHelp]);
    return { variant, isDark, tokens, showHelp, setShowHelp };
};

/**
 * Tokens for wherever this component sits: the modal look inside DialogBody,
 * the screen look everywhere else. Safe without a provider.
 */
export const useDesignVariant = () => useResolved(useContext(SurfaceContext) === 'modal' ? 'doorloop' : 'classic');

/** Modal tokens regardless of position -- for the dialog frame around DialogBody. */
export const useModalDesign = () => useResolved('doorloop');

// ---- layout helpers -----------------------------------------------------------

/**
 * The card flow inside a modal: one column, top to bottom, one surface per
 * topic.
 */
export const ModalCardFlow = ({ children, sx = {} }) => {
    const { tokens } = useDesignVariant();
    return (
        <Box sx={{ columnGap: 2, columns: tokens.modalColumns, '& > *': { breakInside: 'avoid', mb: tokens.flowGap }, ...sx }}>
            {children}
        </Box>
    );
};
ModalCardFlow.propTypes = { children: PropTypes.node, sx: PropTypes.object };

/**
 * Section index for the doorloop variant: lists the cards found inside the
 * dialog body (anything with an id starting with `cfg-card-`) and highlights
 * the one at the reading line of the scroll area. Click = scroll there.
 *
 * Present in EVERY doorloop dialog, also while the body is still loading and
 * when it holds a single card, so the content always starts at the same x and
 * "Toon uitleg" is always in the same place. A dialog with one card lists
 * that card's topics instead (elements with an `id` and a `data-rail-topic`
 * label, e.g. ImageGalleryCard), so the index is still worth its column.
 *
 * Reads the DOM after render instead of asking every modal to declare its
 * sections: the cards already carry stable ids for the config search, and
 * permissions decide at render time which ones exist.
 */
const CARD_SELECTOR = '[id^="cfg-card-"]';
const TOPIC_SELECTOR = '[data-rail-topic][id]';

export const SectionRail = ({ scrollRef, deps = [], t }) => {
    const { tokens, isDark, showHelp, setShowHelp } = useDesignVariant();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);

    const collect = useCallback(() => {
        const root = scrollRef.current;
        if (!root) return;
        const cards = Array.from(root.querySelectorAll(CARD_SELECTOR)).map(el => {
            const titleEl = el.querySelector('.MuiTypography-subtitle1, .MuiTypography-h6');
            return { id: el.id, label: (titleEl?.textContent || el.id).trim(), level: 0, parent: null };
        }).filter(i => i.label);
        let found = cards;
        if (cards.length === 1) {
            const card = root.querySelector(`#${CSS.escape(cards[0].id)}`);
            const topics = card ? Array.from(card.querySelectorAll(TOPIC_SELECTOR)).map(el => ({
                id: el.id,
                label: (el.getAttribute('data-rail-topic') || '').trim(),
                level: 1,
                parent: cards[0].id,
            })).filter(i => i.label) : [];
            found = [...cards, ...topics];
        }
        // only re-render when the index really changed: the observer below
        // fires on every DOM change inside the body, including our own
        setItems(prev => (prev.length === found.length && prev.every((it, i) => it.id === found[i].id && it.label === found[i].label)) ? prev : found);
    }, [scrollRef]);

    // Scan now and again shortly after: the modal bodies are lazy chunks and
    // the dialog fades in, so the cards can land a beat after this commit.
    useLayoutEffect(() => {
        collect();
        const timers = [60, 250, 800].map(ms => window.setTimeout(collect, ms));
        return () => timers.forEach(id => window.clearTimeout(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collect, ...deps]);

    // And keep watching: permissions or a language switch can add/remove cards.
    useEffect(() => {
        const root = scrollRef.current;
        if (!root || typeof MutationObserver === 'undefined') return undefined;
        const mo = new MutationObserver(() => collect());
        mo.observe(root, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [scrollRef, collect]);

    // A click in the rail pins that item until the user scrolls by hand: a short
    // card near the end cannot reach the top, so position alone would highlight
    // a different item than the one just clicked.
    const pinnedRef = useRef(null);

    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return undefined;
        const ids = new Set(items.map(i => i.id));
        const onScroll = () => {
            if (pinnedRef.current) { setActive(pinnedRef.current); return; }
            // The reading line travels with the scroll: at the top of the body
            // it sits at the top edge, at the very end at the bottom edge. With a
            // fixed line at the top, the last (short) cards never become active:
            // fully scrolled down they all fit in the final screen and their tops
            // never get there, so the rail stayed on an earlier card.
            const rect = root.getBoundingClientRect();
            const maxScroll = root.scrollHeight - root.clientHeight;
            const progress = maxScroll > 0 ? Math.min(1, Math.max(0, root.scrollTop / maxScroll)) : 0;
            const line = rect.top + 32 + progress * Math.max(0, root.clientHeight - 32);
            let current = null;
            let bestTop = -Infinity;
            if (root.scrollTop > 2) {
                root.querySelectorAll(`${CARD_SELECTOR}, ${TOPIC_SELECTOR}`).forEach(el => {
                    if (!ids.has(el.id)) return;
                    const top = el.getBoundingClientRect().top;
                    // closest item above the line; strict ">" keeps DOM order on a
                    // tie (two-column flow puts cards side by side)
                    if (top <= line && top > bestTop) { bestTop = top; current = el.id; }
                });
            }
            setActive(current || (items[0] && items[0].id) || null);
        };
        const unpin = () => { pinnedRef.current = null; };
        // The other direction: clicking or tabbing into a topic/card marks it in
        // the rail and holds it there until the user scrolls by hand. Without
        // this a dialog that fits on screen never moves its marker at all.
        const pick = (e) => {
            const target = e.target instanceof Element ? e.target : null;
            const topic = target?.closest(TOPIC_SELECTOR);
            const card = target?.closest(CARD_SELECTOR);
            const hit = (topic && ids.has(topic.id) && topic.id) || (card && ids.has(card.id) && card.id) || null;
            if (!hit) { unpin(); return; }
            pinnedRef.current = hit;
            setActive(hit);
        };
        onScroll();
        root.addEventListener('scroll', onScroll, { passive: true });
        // only real user input releases the pin; the smooth scroll of a rail
        // click fires scroll events but none of these
        root.addEventListener('wheel', unpin, { passive: true });
        root.addEventListener('touchmove', unpin, { passive: true });
        root.addEventListener('keydown', unpin);
        // keydown (Tab) runs before the focus moves, so focusin re-pins on the
        // field that receives it
        root.addEventListener('mousedown', pick);
        root.addEventListener('focusin', pick);
        return () => {
            root.removeEventListener('scroll', onScroll);
            root.removeEventListener('wheel', unpin);
            root.removeEventListener('touchmove', unpin);
            root.removeEventListener('keydown', unpin);
            root.removeEventListener('mousedown', pick);
            root.removeEventListener('focusin', pick);
        };
    }, [scrollRef, items]);

    // Hover hint: the block under the mouse shows in the rail with the same
    // light look as hovering that rail item itself. Only a hint -- it leaves
    // with the mouse and never moves the real marker (that stays with scroll,
    // click and focus). Mouse only: a tap fires an emulated hover that would
    // otherwise stick after the finger is gone.
    const [hovered, setHovered] = useState(null);
    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return undefined;
        const ids = new Set(items.map(i => i.id));
        const over = (e) => {
            if (e.pointerType !== 'mouse') return;
            const target = e.target instanceof Element ? e.target : null;
            const topic = target?.closest(TOPIC_SELECTOR);
            const card = target?.closest(CARD_SELECTOR);
            setHovered((topic && ids.has(topic.id) && topic.id) || (card && ids.has(card.id) && card.id) || null);
        };
        const leave = () => setHovered(null);
        root.addEventListener('pointerover', over);
        root.addEventListener('pointerleave', leave);
        return () => {
            root.removeEventListener('pointerover', over);
            root.removeEventListener('pointerleave', leave);
        };
    }, [scrollRef, items]);

    // keep the marked item in view inside a long rail (scrolls the list only,
    // never the dialog around it)
    const listRef = useRef(null);
    useEffect(() => {
        const list = listRef.current;
        if (!list || !active) return;
        const btn = Array.from(list.querySelectorAll('button[data-rail-id]')).find(b => b.getAttribute('data-rail-id') === active);
        if (!btn) return;
        // the list is position:relative, so offsetTop is already measured from it
        const top = btn.offsetTop;
        const bottom = top + btn.offsetHeight;
        if (top < list.scrollTop) list.scrollTop = top - 4;
        else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 4;
    }, [active]);

    // The running highlight (see jump): a new click cancels the previous one,
    // and closing the dialog mid-way must not leave a timer or listener behind.
    const flashRef = useRef({ anim: null, timer: null, cleanup: null });
    const stopFlash = useCallback(() => {
        const f = flashRef.current;
        if (f.anim) f.anim.cancel();
        if (f.timer) window.clearTimeout(f.timer);
        if (f.cleanup) f.cleanup();
        flashRef.current = { anim: null, timer: null, cleanup: null };
    }, []);
    useEffect(() => stopFlash, [stopFlash]);

    if (!tokens.rail) return null;

    // sft_blue is too dark to read on the dark surface; the lighter SoftTouch
    // blue keeps the selected item legible there
    const accent = isDark ? '#28A9E0' : Colors.sft_blue;

    // A click always shows WHERE the item is: when everything fits on screen
    // there is nothing to scroll to, and after a long scroll the eye still has
    // to find the spot. A card gets the same ring as a search hit, a topic row
    // a soft tint; both fade out on their own. Colour fading is not motion, so
    // it stays with "reduce motion" on -- only the smooth scroll goes.
    const flash = (el, isCard) => {
        if (typeof el.animate !== 'function') return;
        // the topic tint reaches 12px past the row on both sides (into the
        // card padding), so the label does not sit on the edge of the band
        const band = (a) => ({ backgroundColor: `${accent}${a}`, boxShadow: `-12px 0 0 ${accent}${a}, 12px 0 0 ${accent}${a}` });
        const on = isCard ? { boxShadow: `0 0 0 3px ${accent}66` } : band('1F');
        const off = isCard ? { boxShadow: `0 0 0 3px ${accent}00` } : band('00');
        flashRef.current.anim = el.animate(
            [{ ...on, offset: 0 }, { ...on, offset: 0.35 }, { ...off, offset: 1 }],
            { duration: 1600, easing: 'ease-out' },
        );
    };

    const jump = (id) => {
        const root = scrollRef.current;
        const el = root?.querySelector(`#${CSS.escape(id)}`);
        if (!root || !el) return;
        stopFlash();
        pinnedRef.current = id;
        setActive(id);
        const isCard = id.startsWith('cfg-card-');
        const maxScroll = root.scrollHeight - root.clientHeight;
        const target = Math.max(0, Math.min(maxScroll, el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 8));
        if (Math.abs(target - root.scrollTop) < 2) {
            // already where it can get: light it up now
            flash(el, isCard);
            return;
        }
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        root.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
        // light it up once the scroll has landed; `scrollend` where the browser
        // has it, a timer as the fallback (whichever comes first)
        const land = () => { stopFlash(); flash(el, isCard); };
        root.addEventListener('scrollend', land, { once: true });
        flashRef.current.cleanup = () => root.removeEventListener('scrollend', land);
        flashRef.current.timer = window.setTimeout(land, reduce ? 0 : 700);
    };

    const rule = isDark ? RULE.dark : RULE.light;
    const activeItem = items.find(i => i.id === active);
    // a topic is active: its card reads as "you are in here", not as selected
    const activeParent = activeItem?.parent || null;

    return (
        <Box component="nav" aria-label={tr('ui_design_rail_label', 'Onderdelen')} sx={{
            width: tokens.railWidth || 200,
            flexShrink: 0,
            // full height of the body: the list scrolls on its own when a modal
            // has many topics, and the help switch stays on the bottom edge
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            mr: 3,
        }}>
            <Box component="ul" ref={listRef} sx={{ position: 'relative', listStyle: 'none', m: 0, p: 0, flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                {items.map(item => {
                    const isActive = item.id === active;
                    const isTopic = item.level === 1;
                    const holdsActive = !isTopic && item.id === activeParent;
                    // the block under the mouse: the rail's own hover look
                    const isHint = !isActive && item.id === hovered;
                    const hoverLook = { color: accent, backgroundColor: isTopic ? 'transparent' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)') };
                    return (
                        <Box component="li" key={item.id} sx={{
                            // topics hang off their card on a hairline, so the
                            // hierarchy reads without an indent-only guess
                            ...(isTopic ? { ml: 1.5, pl: 1, borderLeft: `1px solid ${isActive ? accent : rule}` } : { mb: 0.25 }),
                        }}>
                            <Box
                                component="button"
                                type="button"
                                data-rail-id={item.id}
                                aria-current={isActive ? 'location' : undefined}
                                onClick={() => jump(item.id)}
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    font: 'inherit',
                                    lineHeight: 1.35,
                                    // the selected card is a small surface in the same
                                    // material as the cards on the right: the index
                                    // points at the page, it is not a second menu bar
                                    border: '1px solid transparent',
                                    borderRadius: 1.5,
                                    backgroundColor: 'transparent',
                                    ...(isTopic
                                        ? { fontSize: '0.8125rem', py: 0.5, px: 1, color: isActive ? accent : 'text.secondary', fontWeight: isActive ? 600 : 400 }
                                        : { fontSize: '0.875rem', py: 0.75, px: 1.25, color: isActive ? accent : 'text.primary', fontWeight: isActive || holdsActive ? 600 : 500 }),
                                    ...(isActive && !isTopic ? { backgroundColor: tokens.surfaceBg, borderColor: rule } : {}),
                                    ...(isHint ? hoverLook : {}),
                                    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                                    '&:hover': isActive && !isTopic ? {} : hoverLook,
                                    '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 1 },
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            >
                                {item.label}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {tokens.helpToggle && (
                <Box sx={{ pt: 1.25, mt: 1, borderTop: `1px solid ${rule}` }}>
                    <FormControlLabel
                        sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', color: 'text.secondary' } }}
                        control={<Switch size="small" checked={!!showHelp} onChange={(e) => setShowHelp(e.target.checked)} />}
                        label={tr('ui_design_help_toggle', 'Toon uitleg')}
                    />
                </Box>
            )}
        </Box>
    );
};
SectionRail.propTypes = { scrollRef: PropTypes.object.isRequired, deps: PropTypes.array, t: PropTypes.func };

/**
 * Body of a config dialog: section rail + a scroll area for the content.
 * Replaces a bare <DialogContent>. Everything inside is on the "modal"
 * surface, so the cards, rows and rail take the modal look.
 */
export const DialogBody = ({ children, deps = [], t, sx = {}, scrollRef: externalRef = null }) => {
    const { tokens } = useModalDesign();
    const innerRef = useRef(null);
    // a caller that needs the scroll node itself (fullscreen editor restoring
    // its scroll position) passes its own ref
    const scrollRef = externalRef || innerRef;

    return (
        <SurfaceContext.Provider value="modal">
            <Box sx={{ display: 'flex', minHeight: 0, flex: 1, ...tokens.dialog.contentSx, ...sx }}>
                <SectionRail scrollRef={scrollRef} deps={deps} t={t} />
                <Box ref={scrollRef} sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pr: tokens.rail ? 1 : 0 }}>
                    {children}
                </Box>
            </Box>
        </SurfaceContext.Provider>
    );
};
DialogBody.propTypes = { children: PropTypes.node, deps: PropTypes.array, t: PropTypes.func, sx: PropTypes.object, scrollRef: PropTypes.object };
